// components/NotaPdfUpload.jsx
//
// PDF de la nota de un intercambio de Mejoras ("Seguimiento de Intercambios").
// Se usa por fila (Etapa I y II). El archivo se guarda en la base (bytea) en
// form_c_mediciones_mejoras_adjuntos, identificado por una `nota_ref` estable
// que genera el front y viaja en la fila del intercambio (ver migración 082).
//
// Ver: el endpoint exige Authorization, así que se hace fetch con el token y se
// muestra el blob en un modal (mismo patrón que DominioPdfUpload).
import React, { useContext, useState } from 'react';
import { AuthContext } from '../context/authContext';

const ROLES_GESTION = ['admin', 'admin_mejoramientos', 'mejoramientos'];
const MAX_BYTES = 10 * 1024 * 1024; // 10MB (mismo límite que multer)

const btnMini = 'px-2 py-1 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors';
const btnMiniDanger = 'px-2 py-1 rounded text-xs font-medium text-white bg-red-600 hover:bg-red-700 transition-colors';

const generarRef = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `ref-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const NotaPdfUpload = ({ codigoObra, etapa, notaRef, nombreArchivo, onRef, onNombreChange }) => {
    const { token, role, roles } = useContext(AuthContext) || {};
    const [refLocal, setRefLocal] = useState('');
    const [subiendo, setSubiendo] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [error, setError] = useState('');

    const rolesUsuario = Array.isArray(roles) && roles.length ? roles : (role ? [role] : []);
    const puedeGestionar = rolesUsuario.some((r) => ROLES_GESTION.includes(r));
    const ref = notaRef || refLocal;

    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const baseUrl = codigoObra && ref
        ? `/api/mejoras/nota-pdf/${encodeURIComponent(codigoObra)}/${etapa}/${encodeURIComponent(ref)}`
        : null;

    const subir = async (file) => {
        setError('');
        if (!file) return;
        if (!codigoObra) {
            setError('Guardá la obra primero para poder adjuntar la nota.');
            return;
        }
        if (file.type !== 'application/pdf') {
            setError('El archivo debe ser un PDF.');
            return;
        }
        if (file.size > MAX_BYTES) {
            setError('El PDF supera los 10MB.');
            return;
        }

        let refParaSubir = ref;
        if (!refParaSubir) {
            refParaSubir = generarRef();
            setRefLocal(refParaSubir);
            onRef?.(refParaSubir);
        }

        setSubiendo(true);
        try {
            const fd = new FormData();
            fd.append('archivo', file);
            const res = await fetch(
                `/api/mejoras/nota-pdf/${encodeURIComponent(codigoObra)}/${etapa}/${encodeURIComponent(refParaSubir)}`,
                { method: 'POST', headers, body: fd }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.message || 'No se pudo subir el PDF');
            onNombreChange?.(data?.data?.nombreArchivo || file.name);
        } catch (e) {
            setError(e.message);
        } finally {
            setSubiendo(false);
        }
    };

    const ver = async () => {
        setError('');
        if (!baseUrl) return;
        try {
            const res = await fetch(`${baseUrl}/archivo`, { headers });
            if (!res.ok) throw new Error('No se pudo abrir el PDF');
            const blob = await res.blob();
            setPdfUrl(URL.createObjectURL(blob));
        } catch (e) {
            setError(e.message);
        }
    };

    const cerrarPdf = () => {
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
    };

    const eliminar = async () => {
        if (!baseUrl) return;
        if (!window.confirm('¿Eliminar el PDF de esta nota?')) return;
        setError('');
        try {
            const res = await fetch(baseUrl, { method: 'DELETE', headers });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.message || 'No se pudo eliminar el PDF');
            onNombreChange?.('');
        } catch (e) {
            setError(e.message);
        }
    };

    if (!puedeGestionar && !nombreArchivo) {
        return <span className="text-xs text-gray-400">—</span>;
    }

    const spinner = (
        <span className="inline-block w-3 h-3 mr-1 align-middle border-2 border-white/50 border-t-white rounded-full animate-spin" />
    );

    return (
        <div className="min-w-[150px]">
            {nombreArchivo ? (
                <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-200">
                        <span className="truncate max-w-[140px]" title={nombreArchivo}>{nombreArchivo}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        <button type="button" onClick={ver} className={btnMini}>Ver</button>
                        {puedeGestionar && (
                            <label className={`${subiendo ? 'opacity-60 cursor-not-allowed ' : 'cursor-pointer '}${btnMini}`}>
                                {subiendo ? <>{spinner}Subiendo…</> : 'Reemplazar'}
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    className="hidden"
                                    disabled={subiendo}
                                    onChange={(e) => { subir(e.target.files?.[0]); e.target.value = ''; }}
                                />
                            </label>
                        )}
                        {puedeGestionar && (
                            <button type="button" onClick={eliminar} className={btnMiniDanger}>Quitar</button>
                        )}
                    </div>
                </div>
            ) : (
                <label className={`${subiendo ? 'opacity-60 cursor-not-allowed ' : 'cursor-pointer '}${btnMini} inline-block`}>
                    {subiendo ? <>{spinner}Subiendo…</> : 'Subir PDF'}
                    <input
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        disabled={subiendo}
                        onChange={(e) => { subir(e.target.files?.[0]); e.target.value = ''; }}
                    />
                </label>
            )}

            {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}

            {pdfUrl && (
                <div
                    className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
                    onClick={cerrarPdf}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[92vw] h-[92vh] max-w-6xl flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Nota del intercambio</p>
                            <div className="flex items-center gap-2">
                                <a href={pdfUrl} download={nombreArchivo || 'nota.pdf'} className={btnMini}>
                                    Descargar
                                </a>
                                <button
                                    type="button"
                                    onClick={cerrarPdf}
                                    className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 text-xl leading-none"
                                    title="Cerrar"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                        <iframe src={pdfUrl} title="Nota del intercambio" className="flex-1 w-full bg-white" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotaPdfUpload;
